import { Zustandspille } from "../zustandspille";
import { feldIds, useFeldId } from "./feld";

/**
 * Schalter und Kontrollkästchen (M-002 F10).
 * - `Schalter`: sofort wirksame Einstellung (kein Speichern-Knopf; der Aufrufer sendet beim Umschalten).
 * - `Kontrollkästchen`: Wert im Formular bzw. Mehrfachauswahl, wirkt erst mit dem Absenden.
 * Beide tragen `label` (Pflicht), optional `hilfe` und – nur Kontrollkästchen/Schalter in Formularen – `fehler` (F3).
 * Pflicht bleibt unmarkiert (F5).
 */
type SteuerRest = Omit<React.InputHTMLAttributes<HTMLInputElement>, "id" | "className" | "style" | "type" | "size" | "children">;
/**
 * `standard`: Schalter links, Label rechts (Formulare, Dialoge). `zeile`: Label/Hilfe links, Schalter rechts (Einstellungslisten, T6).
 * `zelle`: nur der Schalter, der Name (`label`) nur für Hilfstechnik (Matrix Hinweis × Kanal, AW-071).
 */
export type SchalterLayout = "standard" | "zeile" | "zelle";
interface SteuerEigenschaften {
  id?: string;
  label: string;
  hilfe?: string;
  /** Feldfehler (F3), z. B. „Bitte bestätigen“. */
  fehler?: string;
}

const SCHALTER_SPUR =
  "absolute inset-0 bg-surface-1 rounded-full transition-colors duration-200 ease-in-out peer-checked:bg-primary-checked peer-focus-visible:ring-2 peer-focus-visible:ring-primary-focus peer-disabled:opacity-50 peer-disabled:pointer-events-none";
const SCHALTER_KNOPF =
  "absolute top-1/2 inset-s-0.5 -translate-y-1/2 size-5 bg-switch rounded-full shadow-sm transition-transform duration-200 ease-in-out peer-checked:translate-x-full";
// Oberkante des Kästchens (Ausrichtung zur ERSTEN Textzeile, Preline `mt-0.5` bei Zeilenhöhe 20 px = text-sm):
// `zeile` (Standard) → 2 px; `beruehrung` → Beschriftung ist ein Knopf mit `py-3` (44 px Zielhöhe), die erste Zeile
// beginnt dann 12 px tiefer, also 14 px (`mt-3.5`). Ausgeschriebene Klassen, damit Tailwind sie erkennt.
const KONTROLLKAESTCHEN = {
  zeile:
    "shrink-0 mt-0.5 size-4 bg-transparent border-line-3 rounded-sm shadow-2xs text-primary focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-primary-focus checked:bg-primary-checked checked:border-primary-checked disabled:opacity-50 disabled:pointer-events-none",
  beruehrung:
    "shrink-0 mt-3.5 size-4 bg-transparent border-line-3 rounded-sm shadow-2xs text-primary focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-primary-focus checked:bg-primary-checked checked:border-primary-checked disabled:opacity-50 disabled:pointer-events-none",
} as const;
const KONTROLLKAESTCHEN_FEHLER = {
  zeile:
    "shrink-0 mt-0.5 size-4 bg-transparent border-red-500 rounded-sm shadow-2xs text-primary focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-primary-focus checked:bg-primary-checked checked:border-primary-checked disabled:opacity-50 disabled:pointer-events-none",
  beruehrung:
    "shrink-0 mt-3.5 size-4 bg-transparent border-red-500 rounded-sm shadow-2xs text-primary focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-primary-focus checked:bg-primary-checked checked:border-primary-checked disabled:opacity-50 disabled:pointer-events-none",
} as const;
const LABEL = "text-sm text-foreground";
const HILFE = "text-sm text-muted-foreground-1";

function Meldungen({ ids, hilfe, fehler }: { ids: ReturnType<typeof feldIds>; hilfe?: string; fehler?: string }) {
  return (
    <>
      {hilfe && (
        <p id={ids.hilfe} className={HILFE}>
          {hilfe}
        </p>
      )}
      {fehler && (
        <p id={ids.fehler} className="mt-1">
          {/* Abweichung AW-005: Feldfehler V2, Satz in Pille */}
          <Zustandspille ton="gefahr" mitPunkt>{fehler}</Zustandspille>
        </p>
      )}
    </>
  );
}

const ZEILE = "p-4 flex items-center justify-between gap-x-4 border-t border-layer-line first:border-t-0";
const ZEILEN_LISTE = "flex flex-col bg-layer border border-layer-line rounded-xl";
const SCHALTER_HUELLE = "relative inline-block w-11 h-6 shrink-0 cursor-pointer";

/**
 * Hülle für `Schalter layout="zeile"`: Karte mit Trennern zwischen den Zeilen (T6, Einstellungslisten).
 * Der Baustein setzt nur die Hülle, die Zeilen sind `Schalter` mit `layout="zeile"`.
 */
export function Schalterliste({ children, label }: { children: React.ReactNode; label?: string }) {
  return (
    <>
      {/* Preline: forms/forms-sub-core-inputs-layouts/forms-cat-switches/settings-list-with-switches-and-sorting */}
      <div role="group" aria-label={label} className={ZEILEN_LISTE}>
        {children}
      </div>
    </>
  );
}

export function Schalter({
  id: idVorgabe,
  label,
  hilfe,
  fehler,
  layout = "standard",
  ...rest
}: SteuerEigenschaften & SteuerRest & { layout?: SchalterLayout }) {
  const id = useFeldId(idVorgabe);
  const ids = feldIds(id);
  const beschreibung = [hilfe ? ids.hilfe : null, fehler ? ids.fehler : null].filter(Boolean).join(" ") || undefined;
  if (layout === "zelle") {
    // Abweichung AW-071: Schalter allein in einer Matrix-Zelle (Hinweis × Kanal); der Name steht nur für Hilfstechnik (`label`, sr-only).
    return (
      <>
        {/* Preline: component/switch/switch-with-description */}
        <label htmlFor={id} className={SCHALTER_HUELLE}>
          <input
            {...rest}
            id={id}
            type="checkbox"
            role="switch"
            className="peer sr-only"
            aria-label={label}
            aria-describedby={beschreibung}
            aria-invalid={fehler ? true : undefined}
          />
          <span className={SCHALTER_SPUR} />
          <span className={SCHALTER_KNOPF} />
        </label>
      </>
    );
  }
  if (layout === "zeile") {
    return (
      <>
        {/* Preline: forms/forms-sub-core-inputs-layouts/forms-cat-switches/settings-list-with-switches-and-sorting */}
        <div className={ZEILE}>
          <div className="min-w-0">
            <label htmlFor={id} className="block text-sm font-medium text-foreground">
              {label}
            </label>
            <Meldungen ids={ids} hilfe={hilfe} fehler={fehler} />
          </div>
          <label htmlFor={id} className={SCHALTER_HUELLE}>
            <input
              {...rest}
              id={id}
              type="checkbox"
              role="switch"
              className="peer sr-only"
              aria-describedby={beschreibung}
              aria-invalid={fehler ? true : undefined}
            />
            <span className={SCHALTER_SPUR} />
            <span className={SCHALTER_KNOPF} />
          </label>
        </div>
      </>
    );
  }
  return (
    <>
      {/* Preline: component/switch/switch-with-description */}
      <div>
        <div className="flex items-center gap-x-3">
          <label htmlFor={id} className="relative inline-block w-11 h-6 cursor-pointer">
            <input
              {...rest}
              id={id}
              type="checkbox"
              role="switch"
              className="peer sr-only"
              aria-describedby={beschreibung}
              aria-invalid={fehler ? true : undefined}
            />
            <span className={SCHALTER_SPUR} />
            <span className={SCHALTER_KNOPF} />
          </label>
          <label htmlFor={id} className={LABEL}>
            {label}
          </label>
        </div>
        <div className="ms-14">
          <Meldungen ids={ids} hilfe={hilfe} fehler={fehler} />
        </div>
      </div>
    </>
  );
}

const LABEL_ERLEDIGT = "text-sm text-muted-foreground-1 line-through";

export function Kontrollkaestchen({
  id: idVorgabe,
  label,
  hilfe,
  fehler,
  durchgestrichen = false,
  beschriftung,
  beschriftungHoehe = "zeile",
  ...rest
}: SteuerEigenschaften &
  SteuerRest & {
    /**
     * Zeilenhöhe der `beschriftung`, damit das Kästchen an der ersten Textzeile sitzt: `zeile` = eine Textzeile (text-sm,
     * 20 px, Preline-Standard); `beruehrung` = Knopf mit `py-3` (44 px Zielhöhe, Name als Klickziel).
     */
    beschriftungHoehe?: "zeile" | "beruehrung";
    /** Erledigt-Optik der Zeile (Teilaufgaben, M-011): Text grau und durchgestrichen; die Aussage trägt der Haken. */
    durchgestrichen?: boolean;
    /**
     * Ersetzt das `<label>` neben dem Kästchen (Teilaufgaben-Zeile, Betreiber 12.10.2026: nur das Kästchen hakt ab, der Name
     * ist kein Label). Das Kästchen behält seinen Namen per `aria-label={label}`; die Beschriftung ist kein Klickziel des
     * Kästchens (kein `htmlFor`).
     */
    beschriftung?: React.ReactNode;
  }) {
  const id = useFeldId(idVorgabe);
  const ids = feldIds(id);
  const beschreibung = [hilfe ? ids.hilfe : null, fehler ? ids.fehler : null].filter(Boolean).join(" ") || undefined;
  return (
    <>
      {/* Preline: component/checkbox/checkbox-default */}
      <div className="flex gap-x-3">
        <input
          {...rest}
          id={id}
          type="checkbox"
          className={fehler ? KONTROLLKAESTCHEN_FEHLER[beschriftungHoehe] : KONTROLLKAESTCHEN[beschriftungHoehe]}
          aria-label={beschriftung !== undefined ? label : undefined}
          aria-describedby={beschreibung}
          aria-invalid={fehler ? true : undefined}
        />
        <div>
          {beschriftung !== undefined ? (
            beschriftung
          ) : (
            <label htmlFor={id} className={durchgestrichen ? LABEL_ERLEDIGT : LABEL}>
              {label}
            </label>
          )}
          <Meldungen ids={ids} hilfe={hilfe} fehler={fehler} />
        </div>
      </div>
    </>
  );
}
